import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config/appConfig';

const PLAN_LABEL = { MENU_QR: 'Pedidos Web', POS: 'POS', RESTAURANTE: 'Restaurante' };
const PLAN_COLOR = { MENU_QR: '#0891b2', POS: '#7c3aed', RESTAURANTE: '#d97706' };
const ESTADO_COLOR = { AL_DIA: '#16a34a', PENDIENTE: '#d97706', BLOQUEADA: '#dc2626' };
const ESTADO_LABEL = { AL_DIA: 'Al día', PENDIENTE: 'Pendiente', BLOQUEADA: 'Bloqueada' };

function ScoreBadge({ score }) {
    const bg = score === 5 ? '#dcfce7' : score >= 3 ? '#fef9c3' : '#fee2e2';
    const color = score === 5 ? '#15803d' : score >= 3 ? '#854d0e' : '#991b1b';
    return (
        <span style={{ background: bg, color, fontWeight: 700, fontSize: '11px', padding: '2px 8px', borderRadius: '6px', fontVariantNumeric: 'tabular-nums' }}>
            {score}/5
        </span>
    );
}

function FilaNegocio({ n }) {
    const diasLabel = n.dias_restantes === null ? '—' :
        n.dias_restantes < 0 ? `Vencido ${Math.abs(n.dias_restantes)}d` :
        `${n.dias_restantes}d restantes`;
    const diasColor = n.dias_restantes === null ? '#9ca3af' :
        n.dias_restantes < 0 ? '#dc2626' :
        n.dias_restantes <= 7 ? '#d97706' : '#6b7280';

    return (
        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1a1a2e' }}>{n.nombre}</td>
            <td style={{ padding: '12px 8px' }}>
                <span style={{ background: PLAN_COLOR[n.plan] + '20', color: PLAN_COLOR[n.plan], fontWeight: 700, fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}>
                    {PLAN_LABEL[n.plan] || n.plan}
                </span>
            </td>
            <td style={{ padding: '12px 8px' }}>
                <span style={{ background: ESTADO_COLOR[n.estado] + '20', color: ESTADO_COLOR[n.estado], fontWeight: 700, fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}>
                    {ESTADO_LABEL[n.estado] || n.estado}
                </span>
            </td>
            <td style={{ padding: '12px 8px', textAlign: 'center' }}><ScoreBadge score={n.onboarding_score} /></td>
            <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px' }}>{n.num_productos}</td>
            <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px' }}>{n.num_staff}</td>
            <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{n.num_ventas.toLocaleString('es-CL')}</td>
            <td style={{ padding: '12px 8px', fontSize: '12px', color: diasColor, fontVariantNumeric: 'tabular-nums' }}>{diasLabel}</td>
            <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '16px' }}>
                {n.tiene_logo ? '✓' : <span style={{ color: '#dc2626' }}>✗</span>}
            </td>
        </tr>
    );
}

export default function HealthDashboard({ onAuthError }) {
    const [negocios, setNegocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtro, setFiltro] = useState('todos');
    const [busqueda, setBusqueda] = useState('');

    const token = localStorage.getItem('superadmin_token');
    const authHeaders = { headers: { token } };

    useEffect(() => { cargar(); }, []);

    const cargar = async () => {
        setLoading(true); setError(null);
        try {
            const res = await axios.get(config.api_url + '/api/bimba/health', authHeaders);
            if (res.data.status === 'success') setNegocios(res.data.data);
            else setError(res.data.message || 'Error al cargar');
        } catch (err) {
            if (err.response && err.response.status === 401) { onAuthError(); return; }
            setError('No se pudo conectar con el servidor.');
        } finally { setLoading(false); }
    };

    const filtrados = negocios.filter(n => {
        const matchBusqueda = n.nombre.toLowerCase().includes(busqueda.toLowerCase());
        if (filtro === 'sin_ventas') return matchBusqueda && n.num_ventas === 0;
        if (filtro === 'onboarding_incompleto') return matchBusqueda && n.onboarding_score < 5;
        if (filtro === 'por_vencer') return matchBusqueda && n.dias_restantes !== null && n.dias_restantes >= 0 && n.dias_restantes <= 7;
        if (filtro === 'bloqueados') return matchBusqueda && n.estado === 'BLOQUEADA';
        return matchBusqueda;
    });

    const stats = {
        total: negocios.length,
        sinVentas: negocios.filter(n => n.num_ventas === 0).length,
        incompletos: negocios.filter(n => n.onboarding_score < 5).length,
        porVencer: negocios.filter(n => n.dias_restantes !== null && n.dias_restantes >= 0 && n.dias_restantes <= 7).length,
        bloqueados: negocios.filter(n => n.estado === 'BLOQUEADA').length,
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h2 style={{ fontWeight: 800, fontSize: '1.3rem', color: '#1a1a2e', margin: 0 }}>Health de negocios</h2>
                    <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>Onboarding y operación de cada negocio en tiempo real.</p>
                </div>
                <button onClick={cargar} style={{ background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
                    ↻ Actualizar
                </button>
            </div>

            {/* STATS CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                {[
                    { label: 'Total', value: stats.total, color: '#1a1a2e', key: 'todos' },
                    { label: 'Sin ventas', value: stats.sinVentas, color: '#7c3aed', key: 'sin_ventas' },
                    { label: 'Onboarding incompleto', value: stats.incompletos, color: '#d97706', key: 'onboarding_incompleto' },
                    { label: 'Por vencer (7d)', value: stats.porVencer, color: '#ea580c', key: 'por_vencer' },
                    { label: 'Bloqueados', value: stats.bloqueados, color: '#dc2626', key: 'bloqueados' },
                ].map(s => (
                    <div
                        key={s.key}
                        onClick={() => setFiltro(filtro === s.key ? 'todos' : s.key)}
                        style={{ background: filtro === s.key ? s.color : '#fff', border: `2px solid ${filtro === s.key ? s.color : '#e5e7eb'}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <div style={{ fontSize: '22px', fontWeight: 800, color: filtro === s.key ? '#fff' : s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: filtro === s.key ? 'rgba(255,255,255,0.8)' : '#6b7280', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* BUSCADOR */}
            <div style={{ marginBottom: '16px' }}>
                <input
                    type="text"
                    placeholder="Buscar negocio..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ width: '36px', height: '36px', border: '3px solid #e5e7eb', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto' }} />
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
            )}
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '16px', borderRadius: '10px', fontWeight: 600 }}>{error}</div>}

            {!loading && !error && (
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#f9fafb' }}>
                                    {['Negocio', 'Plan', 'Estado', 'Onboarding', 'Productos', 'Staff', 'Ventas', 'Trial', 'Logo'].map(h => (
                                        <th key={h} style={{ padding: '10px 8px', textAlign: h === 'Negocio' ? 'left' : 'center', fontWeight: 700, color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtrados.length === 0 ? (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No hay negocios que coincidan con el filtro.</td></tr>
                                ) : (
                                    filtrados.map(n => <FilaNegocio key={n.id} n={n} />)
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ padding: '10px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#9ca3af' }}>
                        Mostrando {filtrados.length} de {negocios.length} negocios · Score = logo + categorías + productos + staff + al menos 1 venta
                    </div>
                </div>
            )}
        </div>
    );
}
